import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { SnackbarStatus } from '@Utilities/SnackbarConfig'

type openSnackbarArgs = { status?: SnackbarStatus; text: string }
type SnackbarStateArgs = { url?: string; isEmbed: boolean }

/**
 * The return URL is mirrored into sessionStorage.
 *
 * Redux alone was not enough: `handleUnauthorized` writes this and then does a
 * full `window.location.assign`, which rebuilds the store from initialState —
 * so the URL was destroyed by the very navigation it was captured for, and
 * both `VerifyDevice` and `Login` always fell back to the home page. The SPA
 * paths (a guest clicking a gated CTA) never noticed, because those are
 * react-router transitions that keep the store alive.
 *
 * sessionStorage rather than localStorage: a return URL is per-tab and should
 * not leak into a second tab or survive the browser closing.
 */
const RETURN_URL_KEY = 'returnUrl'

const readReturnUrl = (): string | undefined => {
	try {
		return sessionStorage.getItem(RETURN_URL_KEY) ?? undefined
	} catch {
		// Private mode / storage disabled. The SPA path still works.
		return undefined
	}
}

const writeReturnUrl = (url: string | null): void => {
	try {
		if (url === null) sessionStorage.removeItem(RETURN_URL_KEY)
		else sessionStorage.setItem(RETURN_URL_KEY, url)
	} catch {
		// ignore
	}
}

const initialState: SnackbarStateArgs = {
	url: readReturnUrl(),
	isEmbed: false,
}
const UtilsSlice = createSlice({
	name: 'utils',
	initialState,
	reducers: {
		setCurrentURL: (state: SnackbarStateArgs, action: PayloadAction<string>) => {
			state.url = action.payload
			writeReturnUrl(action.payload)
		},
		resetURL: (state: SnackbarStateArgs) => {
			state.url = undefined
			writeReturnUrl(null)
		},
		setEmbed: (state: SnackbarStateArgs, action: PayloadAction<boolean>) => {
			state.isEmbed = action.payload
		},
	},
})

export const { resetURL, setCurrentURL, setEmbed } = UtilsSlice.actions
export default UtilsSlice
